"use client";

import classNames from "classnames";
import {
  forwardRef,
  useEffect,
  useRef, useState,
} from "react";
import HideIf from "util/HideIf";
import PAText from "../typography/PAText/PAText";
import styles from "./accordion.module.scss";

const Accordion = forwardRef((props: any, ref: any) => {
  const {
    accordionBackgroundColor = "#eee",
    titleClassName,
    contentClassName,
    activeContentBorder = false,
    warningBorder,
    className = "",
    onClick,
    contentHeight,
    closeFromOutsideClick,
    isExpandable = true,
    onAccordionToggle,
    allowDynamicHeight = false,
    closeAccordion = false,
    contentClassNameContainer = "",
    headerTextColor = "#444",
    isOpen = "",
  } = props;

  const [isActive, setIsActive] = useState(Boolean(isOpen === "active" || isOpen));
  const [height, setHeight] = useState(isOpen === "active" || isOpen ? "auto" : "calc(0 * var(--safari-root-font, 10px))");
  const [overFlow, setOverFlow] = useState("");
  const content = useRef(null);
  const accordionRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(0);

  function handleToggleAccordion(isActiveValue: boolean) {
    setIsActive(isActiveValue);
    if (onAccordionToggle) {
      onAccordionToggle(Boolean(isActiveValue));
    }
  }

  function handleClickOutside(event: MouseEvent): void {
    if (!isExpandable) {
      return;
    }
    if (closeFromOutsideClick && ref?.current.contains(event?.target)) {
      setTimeout(() => {
        handleToggleAccordion(false);
        setHeight("calc(0 * var(--safari-root-font, 10px))");
      }, 100);
    } else if (closeFromOutsideClick && !accordionRef?.current.contains(event?.target)) {
      handleToggleAccordion(false);
      setHeight("calc(0 * var(--safari-root-font, 10px))");
    }
  }

  useEffect(() => {
    if (closeAccordion) {
      handleToggleAccordion(false);
      setHeight("calc(0 * var(--safari-root-font, 10px))");
    }
  }, [closeAccordion]);

  useEffect(() => {
    if (closeFromOutsideClick) {
      document.addEventListener("mousedown", handleClickOutside, { passive: true });
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // If isOpen is changed from parent, it should open & height should be adjusted
    handleToggleAccordion(isOpen);
    if (props.handleToggleAccordion) {
      props.handleToggleAccordion(isOpen === "active" || isOpen);
    }
    setHeight(isOpen === "active" || isOpen ? (contentHeight || "unset") : "calc(0 * var(--safari-root-font, 10px))");
  }, [isOpen]);

  useEffect(() => {
    if (allowDynamicHeight) {
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  useEffect(() => {
    if (allowDynamicHeight) {
      const checkHeight = () => {
        if (content?.current) {
          const newHeight = content?.current?.scrollHeight;
          if (newHeight !== height && isActive) {
            setHeight(`${newHeight}px`);
          }
        }
      };

      checkHeight();

      const observer = new MutationObserver(checkHeight);

      if (content?.current) {
        observer.observe(content?.current, {
          childList: true,
          subtree: true,
          attributes: true,
        });
      }

      return () => {
        if (content?.current) observer.disconnect();
      };
    }
  }, [height, windowWidth]);

  function toggleAccordion() {
    if (!isExpandable) {
      return;
    }
    handleToggleAccordion(!isActive ? true : "");
    if (props.handleToggleAccordion) {
      props.handleToggleAccordion(isActive === "" || !isActive);
    }
    setHeight(
      isActive === "active" || isActive ? "calc(0 * var(--safari-root-font, 10px))" : `${content.current.scrollHeight}px`,
    );

    // make overflow visible : drop down used in accordion content Ex: searchby part number
    if (props.allowOverflow) {
      setTimeout(() => {
        if (isActive === "active" || isActive) {
          setOverFlow("");
        } else {
          setOverFlow("show-overflow");
        }
      }, 500);
    }

    if (props.focusInput) {
      content.current.getElementsByTagName("input")[0].focus();
    }
  }

  return (
    <div
      className={classNames(className, {
        [styles.content_border]: activeContentBorder,
        [styles.warning_border]: warningBorder,
        [styles.accordion__section]: true,
      })}
      onClick={onClick}
      ref={accordionRef}
      {...props}
    >

      <button
        type="button"
        className={classNames(`${styles.accordion} ${titleClassName}`, {
          [styles.accordion_remove_cursor]: !isExpandable,
        })}
        onClick={toggleAccordion}
        style={{ backgroundColor: accordionBackgroundColor, color: headerTextColor }}
      >
        <div className={classNames(`${styles.accordion_title_container}`, {
          [styles.active]: isActive,
        })}
        >
          {props.iconClass && (
            <i className={`fa ${props.iconClass}`} />
          )}
          <PAText className={styles.accordion__title}>{props.title}</PAText>
        </div>
        <HideIf show={isExpandable}>
          <span className={classNames(`${styles.accordion_arrow}`, {
            [styles.active]: isActive,
          })}
          >
            <i className="fa fa-angle-down" />
          </span>
        </HideIf>
      </button>

      <div
        ref={content}
        style={{ maxHeight: `${height}` }}
        className={`${styles.accordion__content} ${contentClassNameContainer} ${styles[overFlow]} ${isActive ? [styles.active] : ""}`}
      >
        <div className={`${styles.accordion__text} ${contentClassName}`}>{props.content}</div>
      </div>
    </div>
  );
});

Accordion.displayName = "Accordion";
export default Accordion;
